import React, { useState, useEffect } from "react";
import "./Internship.css";

export default function Internship() {
  const [scrolled, setScrolled] = useState(false);
  const [visibleCards, setVisibleCards] = useState([]);

  // Handle scroll effect for navbar/header
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Intersection Observer for card animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleCards((prev) => [...prev, entry.target.dataset.id]);
          }
        });
      },
      { threshold: 0.1 }
    );

    const cards = document.querySelectorAll("[data-animate]");
    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: "🎯",
      title: "Live Projects",
      description: "Work on real industry projects"
    },
    {
      icon: "👨‍🏫",
      title: "Expert Mentors",
      description: "Learn from industry professionals"
    },
    {
      icon: "📜",
      title: "Certification",
      description: "Recognized completion certificates"
    },
    {
      icon: "🚀",
      title: "Placement Support",
      description: "Career assistance & job referrals"
    }
  ];

  const itCourses = [
  "Web Development",
  "Frontend Development",
  "Backend Development",
  "Full Stack Development",
  "UI / UX Design",
  "Mobile App Development",
  "Data Science",
  "Data Analytics",
  "AI & Machine Learning",
  "Cybersecurity",
  "Cloud Computing",
  "DevOps",
  "Software Testing",
  "Blockchain Development",
  "Internet of Things (IoT)"
];


 const businessCourses = [
  "HR & Recruitment",
  "Business Development",
  "Sales & Marketing",
  "Digital Marketing",
  "Content Writing",
  "Operations Management",
  "Finance & Accounting",
  "Project Management",
  "Customer Support",
  "Supply Chain & Logistics Management"
];


  const careerCourses = [
  "Resume Building",
  "Interview Preparation",
  "Corporate Skills",
  "Communication Skills",
  "Leadership Training",
  "Career Guidance",
  "Aptitude & Reasoning Training",
  "Personality Development",
  "Professional Email & Workplace Etiquette",
  "Personal Branding & LinkedIn Optimization"
];

  const eligibility = [
  { icon: "🎓", title: "Students & Freshers" },
  { icon: "👨‍🎓", title: "Final-Year Graduates" },
  { icon: "🔄", title: "Career Switchers" },
  { icon: "💼", title: "Working Professionals" },
  { icon: "🛠️", title: "Diploma & Polytechnic Students" },
  { icon: "📘", title: "Postgraduates" },
  { icon: "🔍", title: "Job Seekers" },
  { icon: "🚀", title: "Entrepreneurs & Startup Aspirants" }
];


  const handleSmoothScroll = (e, targetId) => {
    e.preventDefault();
    const element = document.querySelector(targetId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const isCardVisible = (id) => visibleCards.includes(id);

  return (
    <div className="internship-page">
      {/* HERO */}
      <section className="internship-hero">
        <div className="hero-content">
          <span className="hero-badge">Trusted by 10,000+ Students</span>
          <h1>
            Launch Your Career with
            <br />
            Real-World Experience
          </h1>
          <p>
            Join industry-leading internship programs designed to transform your
            skills and accelerate your career journey.
          </p>
          <a
            href="#courses"
            className="primary-btn"
            onClick={(e) => handleSmoothScroll(e, "#courses")}
          >
            Explore Programs →
          </a>
        </div>
      </section>

      {/* WHY */}
      <section className="why-section">
        <div className="section-header">
          <h2>Why Choose CareerRestorer?</h2>
          <p>Everything you need to succeed in your career</p>
        </div>
        <div className="feature-grid">
          {features.map((feature, idx) => (
            <div
              key={idx}
              className={`feature-card ${
                isCardVisible(`feature-${idx}`) ? "visible" : ""
              }`}
              data-animate
              data-id={`feature-${idx}`}
            >
              <div className="feature-icon">{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* COURSES */}
      <section className="courses-section" id="courses">
        <div className="courses-content">
          <div className="section-header">
            <h2>Internship Programs</h2>
            <p>Choose from our diverse range of career tracks</p>
          </div>

          {/* IT COURSES */}
          <div className="course-category-wrapper">
            <div className="category-header">
              <div className="category-icon it">💻</div>
              <h3>IT & Technology</h3>
            </div>
            <div className="course-grid it">
              {itCourses.map((course, idx) => (
                <div
                  key={idx}
                  className={`course-card ${
                    isCardVisible(`it-${idx}`) ? "visible" : ""
                  }`}
                  data-animate
                  data-id={`it-${idx}`}
                  onClick={() => console.log(`Selected: ${course}`)}
                >
                  {course}
                </div>
              ))}
            </div>
          </div>

          {/* BUSINESS COURSES */}
          <div className="course-category-wrapper">
            <div className="category-header">
              <div className="category-icon business">📊</div>
              <h3>Business & Management</h3>
            </div>
            <div className="course-grid business">
              {businessCourses.map((course, idx) => (
                <div
                  key={idx}
                  className={`course-card ${
                    isCardVisible(`business-${idx}`) ? "visible" : ""
                  }`}
                  data-animate
                  data-id={`business-${idx}`}
                  onClick={() => console.log(`Selected: ${course}`)}
                >
                  {course}
                </div>
              ))}
            </div>
          </div>

          {/* CAREER COURSES */}
          <div className="course-category-wrapper">
            <div className="category-header">
              <div className="category-icon career">🎓</div>
              <h3>Career Development</h3>
            </div>
            <div className="course-grid career">
              {careerCourses.map((course, idx) => (
                <div
                  key={idx}
                  className={`course-card ${
                    isCardVisible(`career-${idx}`) ? "visible" : ""
                  }`}
                  data-animate
                  data-id={`career-${idx}`}
                  onClick={() => console.log(`Selected: ${course}`)}
                >
                  {course}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ELIGIBILITY */}
      <section className="eligibility-section">
        <div className="section-header">
          <h2>Who Can Apply?</h2>
          <p>Open to everyone ready to grow</p>
        </div>
        <div className="eligibility-grid">
          {eligibility.map((item, idx) => (
            <div
              key={idx}
              className={`eligibility-card ${
                isCardVisible(`eligibility-${idx}`) ? "visible" : ""
              }`}
              data-animate
              data-id={`eligibility-${idx}`}
            >
              <div className="eligibility-icon">{item.icon}</div>
              <div>{item.title}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="internship-cta">
        <div className="cta-content">
          <h2>Ready to Start Your Journey?</h2>
          <p>
            Join thousands of successful interns who launched their careers with
            CareerRestorer. Apply today and transform your future.
          </p>
          <a href="mailto:info@careerrestorer.com" className="cta-btn">
            Apply Now - It's Free!
          </a>
        </div>
      </section>
    </div>
  );
}